import _ from 'lodash';
import {
  addAsset,
  addAttribute,
  addCalculationChildToAsset,
  addChildToAsset,
  addEmptyAsset,
  removeAttribute,
  removeChildFromAsset,
  restoreChild,
  updateAssetName,
  updateChildParameter,
  updateColumnName,
} from '@/assetGroupEditor/assetGroupAssets.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import { Store } from '@/core/flux.service';
import type {
  AssetGroup,
  AssetGroupAsset,
  AssetGroupChild,
  AssetGroupStoreState,
} from '@/assetGroupEditor/assetGroup.types';
import type { PersistenceLevel } from '@/core/flux.service';

export type AssetGroupDehydratedState = Omit<AssetGroupStoreState, 'nameError'>;

export class AssetGroupStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqAssetGroupStore';

  initialize() {
    const initialState: AssetGroupStoreState = {
      id: '',
      assets: [],
      name: '',
      description: '',
      errors: [],
      isLoading: false,
      hasUnsavedChanges: false,
      nameError: false,
      addedBy: '',
    };
    this.state = this.immutable(initialState);
  }

  get id(): string {
    return this.state.get('id');
  }

  get assets(): AssetGroupAsset[] {
    return this.state.get('assets');
  }

  get name(): string {
    return this.state.get('name');
  }

  get nameError(): boolean {
    return this.state.get('nameError');
  }

  get description(): string {
    return this.state.get('description');
  }

  get errors(): string[] {
    return this.state.get('errors');
  }

  get isLoading(): boolean {
    return this.state.get('isLoading');
  }

  get hasUnsavedChanges(): boolean {
    return this.state.get('hasUnsavedChanges');
  }

  private setAssets(assets: AssetGroupAsset[]) {
    this.state.set('assets', assets);
  }

  protected readonly handlers = {
    ASSET_GROUP_SET_ROOT: (assetGroup: AssetGroup) => {
      this.state.merge({ ...assetGroup });
    },
    ASSET_GROUP_ADD_EMPTY_ASSET: (asset: AssetGroupAsset) => {
      // adds a new asset from asset group toolbar button click, with empty 'item' and calculated children
      this.setAssets(addEmptyAsset(this.assets, asset, sqWorkbenchStore.currentUser.id));
    },
    ASSET_GROUP_ADD_ASSET: (asset: AssetGroupAsset) => {
      this.setAssets(addAsset(this.assets, asset));
    },
    ASSET_GROUP_UPDATE_CHILD_PARAMETER: ({
      asset,
      child,
      parameterItemName,
    }: {
      asset: AssetGroupAsset;
      child: AssetGroupChild;
      parameterItemName: string;
    }) => {
      this.setAssets(updateChildParameter(this.assets, asset, child, parameterItemName));
    },
    ASSET_GROUP_RESTORE_CHILD: ({
      asset,
      name,
      newItemId,
    }: {
      asset: AssetGroupAsset;
      name: string;
      newItemId: string;
    }) => {
      this.setAssets(restoreChild(this.assets, asset, name, newItemId));
    },
    ASSET_GROUP_ADD_CHILD_TO_ASSET: ({ asset, child }: { asset: AssetGroupAsset; child: AssetGroupChild }) => {
      this.setAssets(addChildToAsset(this.assets, asset, child));
    },
    ASSET_GROUP_ADD_CALCULATION_CHILD_TO_ASSET: ({
      asset,
      child,
    }: {
      asset: AssetGroupAsset;
      child: AssetGroupChild;
    }) => {
      this.setAssets(addCalculationChildToAsset(this.assets, asset, child));
    },
    ASSET_GROUP_SET_NAME: ({ name }: { name: string }) => {
      this.state.set('name', name);
    },
    ASSET_GROUP_SET_NAME_ERROR: ({ error }: { error: string }) => {
      this.state.set('nameError', error);
    },
    ASSET_GROUP_SET_DESCRIPTION: ({ description }: { description: string }) => {
      this.state.set('description', description);
    },
    ASSET_GROUP_ADD_ATTRIBUTE: (newChild: AssetGroupChild) => {
      this.setAssets(addAttribute(this.assets, newChild));
    },
    ASSET_GROUP_UPDATE_COLUMN_NAME: ({ originalName, newName }: { originalName: string; newName: string }) => {
      this.setAssets(updateColumnName(this.assets, originalName, newName));
    },
    ASSET_GROUP_UPDATE_ASSET_NAME: ({ original, newName }: { original: AssetGroupAsset; newName: string }) => {
      this.setAssets(updateAssetName(this.assets, original, newName));
    },
    ASSET_GROUP_RESET_STORE: () => {
      const resetState: AssetGroupStoreState = {
        id: '',
        assets: [],
        name: '',
        description: '',
        errors: [],
        isLoading: false,
        hasUnsavedChanges: false,
        nameError: false,
        addedBy: '',
      };
      this.state.set(resetState);
    },
    ASSET_GROUP_REMOVE_CHILD_FROM_ASSET: ({ asset, child }: { asset: AssetGroupAsset; child: AssetGroupChild }) => {
      this.setAssets(removeChildFromAsset(this.assets, asset, child));
    },
    ASSET_GROUP_REMOVE_ASSET: ({ name }: { name: string }) => {
      const assets = [...this.state.get('assets')];
      _.remove(assets, ['name', name]);
      this.state.set('assets', assets);
    },
    ASSET_GROUP_REMOVE_ATTRIBUTE: ({ name }: { name: string }) => {
      this.setAssets(removeAttribute(this.assets, name));
    },
    ASSET_GROUP_SET_IS_LOADING: ({ isLoading }: { isLoading: boolean }) => {
      this.state.set('isLoading', isLoading);
    },
    ASSET_GROUP_SET_HAS_UNSAVED_CHANGES: ({ hasUnsavedChanges }: { hasUnsavedChanges: boolean }) => {
      this.state.set('hasUnsavedChanges', hasUnsavedChanges);
    },
  };

  dehydrate(): AssetGroupDehydratedState {
    return _.omit(this.state.serialize(), 'nameError');
  }

  rehydrate(dehydratedState: Partial<AssetGroupDehydratedState>): void {
    this.state.merge(dehydratedState);
  }
}
